import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CustomerListComponent, CustomerSelectedEvent } from '../../components/customer-list.component/customer-list.component';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { CustomerSelectionService } from '../../services/customer-selection';
import { RouteSelectionService } from '../../services/route-selection';
import { MessageDialogService } from '../../services/message-dialog';
import { CustomerFavoritesService, LastPriceRow } from '../../services/customer-favorites-service';
import { CartService } from '../../services/cart';
import { SelectedProduct } from '../../services/products';

@Component({
  selector: 'app-select-customer-component',
  imports: [CustomerListComponent],
  templateUrl: './select-customer-component.html',
  styleUrl: './select-customer-component.scss',
  standalone: true
})
export class SelectCustomerComponent implements OnInit, OnDestroy {
  selectedRouteIds: number[] | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private routeService: RouteSelectionService,
    private customerService: CustomerSelectionService,
    private dialog: MessageDialogService,
    private favoritesService: CustomerFavoritesService,
    private cartService: CartService
  ) { }

  ngOnInit(): void {
    this.routeService.selection$
      .pipe(takeUntil(this.destroy$))
      .subscribe(route => {
        this.selectedRouteIds = route?.lineIds ?? [];
      });
  }

  async handleCustomerSelected(event: CustomerSelectedEvent | null): Promise<void> {
    if (!event?.customer) {
      return;
    }

    const customer = event.customer;
    const requestedMode = event.loadMode || 'ask';
    this.customerService.setSelected(customer, true);

    // Offline-created customers do not have server sales history yet.
    if (customer.pendingSync || customer.id <= 0) {
      this.router.navigate(['/order']);
      return;
    }

    try {
      const rows = await firstValueFrom(this.favoritesService.getLastPricesStrict({
        customer_id: customer.id
      }));

      if (requestedMode === 'last_invoice') {
        await this.loadLastInvoiceFromRecentItems(customer.id, rows);
        this.router.navigate(['/order']);
        return;
      }

      if (rows?.length) {
        const choice = await firstValueFrom(this.dialog.choose(
          'Do you want to load the recently bought items?',
          'Load Items',
          [
            { label: 'Skip', value: 'skip', color: 'warn' },
            { label: 'Load Recent Items', value: 'recent', color: 'primary', raised: true },
          ]
        ));

        if (choice === 'recent') {
          await this.loadRecentItems(customer.id, rows);
        }
      }
    } catch (err) {
      console.error('Failed to load customer items', err);
      const message = err instanceof Error ? err.message : 'Unable to load customer items.';
      await firstValueFrom(this.dialog.error(message, 'Load Items'));
    }

    this.router.navigate(['/order']);
  }

  private async loadRecentItems(customerId: number, rows: LastPriceRow[]): Promise<void> {
    try {
      await firstValueFrom(this.favoritesService.cacheRows(customerId, rows));

      // Build cart from items with avg_qty_30d > 0
      const prefill: SelectedProduct[] = rows
        .filter((r: LastPriceRow) => (r.avg_qty_30d || 0) > 0)
        .map((r: LastPriceRow) => ({
          productId: r.product.id,
          quantity: Math.max(1, Math.round(r.avg_qty_30d || 1)),
          price: r.last_price
        }));

      if (prefill.length) {
        await this.cartService.updateCart(prefill);
      }
    } catch (err) {
      console.error('Failed to load recent items', err);
      throw err;
    }
  }

  private async loadLastInvoiceFromRecentItems(customerId: number, rows: LastPriceRow[]): Promise<void> {
    if (!rows?.length) {
      await firstValueFrom(this.dialog.info('No previous sales invoice found for this customer.', 'Last Invoice'));
      return;
    }

    await firstValueFrom(this.favoritesService.cacheRows(customerId, rows));

    const rowsWithInvoice = rows.filter(row => !!row.invoice?.id);
    let selectedRows: LastPriceRow[] = [];

    if (rowsWithInvoice.length) {
      const latestInvoiceKey = rowsWithInvoice
        .map(row => ({
          invoiceId: Number(row.invoice?.id || 0),
          dateKey: this.toDateKey(row.invoice?.date || row.last_sale_date),
        }))
        .sort((a, b) => {
          if (b.dateKey !== a.dateKey) {
            return b.dateKey - a.dateKey;
          }
          return b.invoiceId - a.invoiceId;
        })[0];

      selectedRows = rowsWithInvoice.filter(row => Number(row.invoice?.id || 0) === latestInvoiceKey.invoiceId);
    } else {
      const latestSaleDateKey = Math.max(...rows.map(row => this.toDateKey(row.last_sale_date)));
      selectedRows = rows.filter(row => this.toDateKey(row.last_sale_date) === latestSaleDateKey);
    }

    const prefill: SelectedProduct[] = selectedRows
      .filter((row: LastPriceRow) => !!row.product?.id && (row.last_qty || 0) > 0)
      .map((row: LastPriceRow) => ({
        productId: row.product.id,
        quantity: Number(row.last_qty || 0),
        price: Number(row.last_price || 0)
      }));

    if (!prefill.length) {
      await firstValueFrom(this.dialog.info('No product lines found in the last invoice.', 'Last Invoice'));
      return;
    }

    await this.cartService.updateCart(prefill);
  }

  private toDateKey(value: string | null | undefined): number {
    if (!value) {
      return 0;
    }

    const timestamp = new Date(value).getTime();
    return Number.isFinite(timestamp) ? timestamp : 0;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
